import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Text, TextInput, Button, useTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { AuthenticationService } from '../services/AuthenticationService';

interface LoginForm {
  email: string;
  password: string;
}

export default function LoginScreen({ navigation }: any) {
  const { colors } = useTheme();
  const [form, setForm] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<LoginForm>>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: Partial<LoginForm> = {};

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!form.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await AuthenticationService.login(form.email, form.password);
      // Navigate to the chat screen
      navigation.replace('Chat');
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific Firebase auth errors
      let errorMessage = 'An error occurred during login';
      
      switch (error.message) {
        case 'Firebase: Error (auth/user-not-found).':
        case 'Firebase: Error (auth/wrong-password).':
          errorMessage = 'Invalid email or password';
          setErrors(prev => ({ 
            ...prev, 
            email: errorMessage,
            password: errorMessage 
          }));
          break;
        case 'Firebase: Error (auth/invalid-email).':
          errorMessage = 'Invalid email address';
          setErrors(prev => ({ ...prev, email: errorMessage }));
          break;
        case 'Firebase: Error (auth/too-many-requests).':
          errorMessage = 'Too many failed attempts. Please try again later';
          Alert.alert('Login Error', errorMessage);
          break;
        default:
          Alert.alert('Login Error', errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // TODO: Implement forgot password functionality
    Alert.alert(
      'Forgot Password',
      'Password reset functionality will be available soon.',
      [{ text: 'OK' }]
    );
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      const userCredential = await AuthenticationService.signInWithGoogle();
      if (userCredential) {
        navigation.replace('Chat');
      }
    } catch (error: any) {
      Alert.alert('Google Sign-In Error', error.message || 'An error occurred during Google sign-in');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: '#111' }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <Text style={styles.title}>{'Astra'}</Text>
            <Text style={styles.subtitle}>Your Personal Astrology Assistant</Text>
          </View>
        </View>

        <View style={styles.formContainer}>
          <Button
            mode="outlined"
            icon={() => <Ionicons name="logo-google" size={22} color="#fff" />}
            onPress={handleGoogleLogin}
            disabled={isLoading}
            style={{ marginTop: 16, borderColor: '#4285F4', borderWidth: 1, backgroundColor: '#232323' }}
            contentStyle={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8 }}
            labelStyle={{ color: '#fff', fontWeight: '600', fontSize: 16 }}
          >
            {isLoading ? <ActivityIndicator color="#fff" /> : 'Sign in with Google'}
          </Button>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#111',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
  },
  header: {
    alignItems: 'center',
    marginTop: 60,
    marginBottom: 40,
  },
  headerContent: {
    alignItems: 'center',
  },
  title: {
    fontSize: 48,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 8,
    fontFamily: 'Georgia',
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 20,
    color: '#B0B0B0',
    fontWeight: '600',
    fontFamily: 'System',
  },
  formContainer: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  input: {
    marginBottom: 8,
    backgroundColor: '#181818',
    borderColor: '#232323',
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    marginBottom: 16,
    marginLeft: 4,
  },
  forgotPassword: {
    alignSelf: 'flex-end',
    marginTop: 8,
    marginBottom: 24,
  },
  forgotPasswordText: {
    color: '#B0B0B0',
    fontSize: 14,
  },
  loginButton: {
    backgroundColor: '#181818',
    borderWidth: 1,
    borderColor: '#232323',
  },
  loginButtonContent: {
    paddingVertical: 8,
  },
  signupLink: {
    marginTop: 24,
    alignItems: 'center',
  },
  signupText: {
    color: '#B0B0B0',
    fontSize: 16,
  },
  signupTextBold: {
    color: '#fff',
    fontWeight: '600',
  },
}); 
